import {Box, Flex, HStack, Image, SimpleGrid, Stack, Text, useDisclosure} from '@chakra-ui/react';
import {colors} from 'components/providers';
import React, {FC} from 'react';
import {ICartItem, IProduct, IRestaurantResponse} from 'types';
import {v4 as uuidv4} from 'uuid';

import {useDesktop, useTranslation} from '../../../../../hooks';
import DiscountLabel from '../../../../metro/components/DiscountLabel';
import {MetroFavoriteButton} from '../../../../metro/components/metro-favorite-button/MetroFavoriteButton';
import {ProductCartControls} from '../../../../metro/components/product-cart-controls/ProductCartControls';
import {useMetroCart} from '../../../../metro/hooks/useMetroCart';
import {AlertClearCart} from '../../../../pages/restaurant';
import {Price} from '../../../price';

interface Props {
  item: IProduct;
  provider?: IRestaurantResponse;
  quantity: number;
  cartItemId?: string;
  onProductClick?: (product: IProduct) => void;
}

export const MetroSearchResultItem: FC<Props> = ({item, provider, cartItemId, quantity, onProductClick}) => {
  const {isOpen: isOpenAlertClearCart, onOpen: onOpenAlertClearCart, onClose: onCloseAlertClearCart} = useDisclosure();
  const {id, name, price, stock_quantity, product_img_for_detail_thumbnail, is_weighted_product, old_price} = item;
  const {t} = useTranslation();
  const isDesktop = useDesktop();

  const {
    handleAddProduct,
    handleDelete,
    clearCart,
    cartProvider,
    addProvider,
    setCartItems,
    isSelected,
    localQuantity,
  } = useMetroCart({
    product: item,
    provider,
    quantity,
    cartItemId,
    onOpenAlertClearCart: onOpenAlertClearCart,
  });

  const displayQuantity = localQuantity || quantity;

  const isNoStock = stock_quantity === 0;

  const handleItemClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onProductClick) {
      onProductClick(item);
    }
  };

  return (
    <>
      <SimpleGrid
        columns={[2, 4]}
        spacing={3}
        _hover={isDesktop ? {bg: colors.lightGray} : undefined}
        cursor="pointer"
        alignItems="center"
        p={['10px 10px', '10px 15px']}
        templateColumns={['50px 1fr', '50px 1fr 0.5fr 0.3fr']}
        borderBottom="1px"
        borderColor={colors.borderGray}
        onClick={handleItemClick}
        sx={{
          WebkitTapHighlightColor: 'transparent',
          '&:active': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Box position="relative">
          <Image
            w="50px"
            h="50px"
            mr="15px"
            src={product_img_for_detail_thumbnail}
            objectFit="contain"
            fallback={
              <Image
                src="/food_pattern.webp"
                borderRadius="8px"
                alt="fallback"
                objectFit="cover"
                w="50px"
                h="50px"
                mr="15px"
              />
            }
            alt="find"
          />
          {isNoStock && (
            <Text
              position="absolute"
              top={['-1', '0']}
              left="3"
              transform="translate(-25%, -50%)"
              fontSize={['10px', '12px']}
              fontWeight="600"
              p="1px 4px"
              bgColor={colors.inputBgGray1}
              color={colors.white}
              borderRadius="4px"
              zIndex={1500}
              whiteSpace="nowrap"
            >
              {t('NOT_AVAILABLE')}
            </Text>
          )}
          <DiscountLabel oldPrice={old_price} price={price} bottom={0} left={-2} />
        </Box>
        <Text fontWeight={600} fontSize="sm" color={isNoStock ? colors.textGray : colors.black} noOfLines={2}>
          {name}
        </Text>
        {!isDesktop && (
          <Box display="flex" justifyContent="space-between" alignItems="center" gridColumn="span 2">
            {old_price ? (
              <Flex alignItems="center">
                <Stack direction="row" alignItems="center" justifyContent="center" textAlign="center">
                  <Text fontSize="sm" textDecoration="line-through" color={colors.textGray} fontWeight="normal">
                    {old_price}
                  </Text>
                  <Price
                    value={price}
                    priceProps={{
                      fontWeight: 'semibold',
                      fontSize: 'sm',
                      color: isNoStock ? colors.textGray : colors.pink,
                    }}
                  />
                </Stack>
              </Flex>
            ) : (
              <Flex>
                <Price
                  priceProps={{
                    fontWeight: '600',
                    textAlign: 'center',
                    fontSize: 'sm',
                    color: isNoStock ? colors.textGray : undefined,
                  }}
                  value={old_price ? old_price : price}
                  showTextPrice={price < 0.1}
                />
              </Flex>
            )}
            <HStack maxW="50%">
              <ProductCartControls
                isSmall={true}
                isSelected={isSelected}
                quantity={displayQuantity}
                massMeasurement="шт"
                onAdd={handleAddProduct}
                onDelete={handleDelete}
                cartItemId={cartItemId}
                disabled={isNoStock}
                isWeightProduct={is_weighted_product}
              />
              <Box zIndex={100}>
                <MetroFavoriteButton productId={item.id} size={20} />
              </Box>
            </HStack>
          </Box>
        )}
        {isDesktop && (
          <>
            <Box minW="auto" display="flex" justifyContent="flex-end">
              <ProductCartControls
                isSmall={true}
                isSelected={isSelected}
                quantity={displayQuantity}
                massMeasurement="шт"
                onAdd={handleAddProduct}
                onDelete={handleDelete}
                cartItemId={cartItemId}
                disabled={isNoStock}
                isWeightProduct={is_weighted_product}
              />
            </Box>
            <Box textAlign="right">
              {old_price ? (
                <Flex alignItems="center" justifyContent="space-between">
                  <Stack direction="column" alignItems="center" justifyContent="center" textAlign="center">
                    <Text fontSize="sm" textDecoration="line-through" color={colors.textGray} fontWeight="normal">
                      {old_price}
                    </Text>
                    <Price
                      value={price}
                      priceProps={{
                        fontWeight: 'semibold',
                        fontSize: 'sm',
                        color: isNoStock ? colors.textGray : colors.pink,
                      }}
                    />
                  </Stack>
                  <Box zIndex={100}>
                    <MetroFavoriteButton productId={item.id} size={20} />
                  </Box>
                </Flex>
              ) : (
                <Flex justifyContent="space-between">
                  <Price
                    priceProps={{
                      fontWeight: '600',
                      textAlign: 'center',
                      fontSize: 'sm',
                      color: isNoStock ? colors.textGray : undefined,
                    }}
                    value={old_price ? old_price : price}
                    showTextPrice={price < 0.1}
                  />
                  <MetroFavoriteButton productId={item.id} size={20} />
                </Flex>
              )}
            </Box>
          </>
        )}
      </SimpleGrid>
      {isOpenAlertClearCart && (
        <AlertClearCart
          isOpen={isOpenAlertClearCart}
          onClose={onCloseAlertClearCart}
          onConfirm={() => {
            clearCart(true);
            let productTracking = 'restaurant/';
            // Tracking product origin - added from carousel, crosseling, recommended
            const isFromReccomended = provider?.recommended_products.find((item) => item.id === id);
            productTracking += isFromReccomended ? 'restaurant_recommended' : 'restaurant';

            const newCartItem: ICartItem = {
              id: uuidv4(),
              product: item,
              productId: id,
              price: price,
              quantity: 1,
              instructions: '',
              selectedOptions: {},
              added_from: productTracking,
            };
            setCartItems([newCartItem]);
            if (provider) addProvider(provider);
          }}
          providerName={cartProvider?.name}
        />
      )}
    </>
  );
};
